// Read-only observation surface over the live editor state.
// Node-testable by design: no ScratchJr/document/window globals and no network
// code. Project structure is walked exclusively through web/ai/dsl/projectToDSL.js
// so there is a single source of truth for the page/sprite/script tree.

const {projectToDSL} = require('../dsl/projectToDSL');
const {DELTA_TYPES, DELTA_TYPE_VALUES} = require('./deltaTypes');

const state = {
    source: null,
    listeners: [],
    lastAction: null,
    lastBlockPlacedAt: null
};

function deepFreeze (value) {
    if (value === null || typeof value !== 'object' || Object.isFrozen(value)) {
        return value;
    }
    Object.freeze(value);
    for (const key of Object.keys(value)) {
        deepFreeze(value[key]);
    }
    return value;
}

// Wrap the supplied source in a read-only facade so observation can never reach
// back through it to mutate editor state.
function configure (opts) {
    const src = opts && opts.source;
    state.source = src ? {
        getProject: () => src.getProject(),
        getActivityId: () => src.getActivityId(),
        getCurrentPageIndex: () => src.getCurrentPageIndex(),
        getCurrentSpriteId: () => src.getCurrentSpriteId(),
        getPaletteAvailable: () => src.getPaletteAvailable()
    } : null;
    // Set unconditionally (even when opts.source is absent) so idleMs ticks from configure(), not the first block-placed delta — matches spec §2 and ActivityState.test.js:162-166.
    state.lastBlockPlacedAt = Date.now();
}

function emptySnapshot () {
    return deepFreeze({
        activityId: null,
        currentPage: 0,
        currentSpriteId: null,
        pages: [],
        palette: {available: []},
        lastAction: null,
        idleMs: 0
    });
}

function observe () {
    if (!state.source) {
        return emptySnapshot();
    }
    const palette = state.source.getPaletteAvailable();
    return deepFreeze({
        activityId: state.source.getActivityId(),
        currentPage: state.source.getCurrentPageIndex(),
        currentSpriteId: state.source.getCurrentSpriteId(),
        pages: projectToDSL(state.source.getProject()).pages,
        palette: {available: Array.isArray(palette) ? palette.slice() : []},
        lastAction: state.lastAction ? Object.assign({}, state.lastAction) : null,
        idleMs: state.lastBlockPlacedAt ? Date.now() - state.lastBlockPlacedAt : 0
    });
}

function getCurrentSpriteId () {
    return state.source ? state.source.getCurrentSpriteId() : null;
}

function onChange (cb) {
    if (typeof cb !== 'function') {
        return function noop () {};
    }
    state.listeners.push(cb);
    return function unsubscribe () {
        const idx = state.listeners.indexOf(cb);
        if (idx > -1) {
            state.listeners.splice(idx, 1);
        }
    };
}

function notify (delta) {
    if (!delta || DELTA_TYPE_VALUES.indexOf(delta.type) === -1) {
        return;
    }
    state.lastAction = {
        type: delta.type,
        blockType: delta.blockType,
        spriteId: delta.spriteId,
        at: delta.at || Date.now()
    };
    if (delta.type === DELTA_TYPES.BLOCK_PLACED) {
        state.lastBlockPlacedAt = state.lastAction.at;
    }
    for (const cb of state.listeners.slice()) {
        try {
            cb(delta);
        } catch (e) {
            // Isolate listener errors: a bad subscriber must not break dispatch
            // for the others or the editor flow that triggered the delta.
        }
    }
}

function reset () {
    state.source = null;
    state.listeners = [];
    state.lastAction = null;
    state.lastBlockPlacedAt = null;
}

module.exports = {
    observe,
    getCurrentSpriteId,
    onChange,
    notify,
    configure,
    reset,
    DELTA_TYPES
};
